/**
 * VoiceRecorder — mic → 16kHz mono WAV.
 *
 * Browsers' MediaRecorder produces webm/opus, which Gemini's audio input does
 * NOT accept (it takes wav/mp3/aiff/aac/ogg/flac). So we capture raw PCM via the
 * Web Audio API and encode a small WAV (16kHz mono, 16-bit) ourselves — reliable
 * cross-browser and Gemini-compatible. ~62s fits the 2MB attachment cap.
 */
const TARGET_RATE = 16000;

/** Reason a take produced no usable audio. `silent-bluetooth` means the stream
 *  connected but never delivered a non-zero sample — the classic Hands-Free
 *  Profile (HFP) dead-mic case; `silent` is the generic muted/dead-mic case. */
export type NoAudioReason = "silent" | "silent-bluetooth";

/** True when a track's device label looks like a Bluetooth headset. Plain
 *  substring checks (lower-cased) — no regex. Labels are best-effort and only
 *  populated after permission is granted, so this is a hint, never a gate. */
function labelLooksBluetooth(label: string): boolean {
  const l = label.toLowerCase();
  const needles = ["bluetooth", "airpods", "headset", "hands-free", "hands free", "hfp", "wireless", "bt "];
  for (const n of needles) { if (l.indexOf(n) !== -1) return true; }
  return false;
}

/** User-facing message for a no-audio outcome. Bluetooth gets a targeted hint
 *  because HFP silence is invisible otherwise (the mic "works", just mute). */
export function noAudioMessage(reason: NoAudioReason): string {
  return reason === "silent-bluetooth"
    ? "No audio from your Bluetooth mic — it may be in call/hands-free mode. Try your built-in mic, or reconnect the headset."
    : "No audio detected — check that your microphone isn't muted.";
}

/** Map a getUserMedia failure to plain, actionable user guidance. Branches on
 *  the DOMException .name plus the insecure-context / undefined-API cases. */
export function micErrorMessage(err: unknown): string {
  // getUserMedia missing entirely → insecure context (http://) or old browser.
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== "function") {
    return window.isSecureContext === false
      ? "Microphone needs a secure (https) page — recording isn't available here."
      : "This browser doesn't support microphone recording.";
  }
  const name = (err as { name?: string })?.name || "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Microphone access was blocked — allow it in your browser's site settings.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "No microphone was found — plug one in and try again.";
    case "NotReadableError":
    case "AbortError":
      return "The microphone is busy (in use by another app) — close it and retry.";
    default:
      return "Couldn't start the microphone — check your device and permissions.";
  }
}

export class VoiceRecorder {
  private stream: MediaStream | null = null;
  private ctx: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private analyser: AnalyserNode | null = null;
  private levelBuf: Uint8Array<ArrayBuffer> | null = null;
  private chunks: Float32Array[] = [];
  private inRate = 48000;
  /** Whether the active input device looks like a Bluetooth headset (label
   *  heuristic). Drives the Bluetooth-specific no-audio message. */
  private isBluetooth = false;
  /** Flips true the first time onaudioprocess sees a non-trivial sample. Stays
   *  false for an HFP-silent / dead track even though the track reads "live". */
  private sawAudio = false;
  /** Fired when the OS input-device set changes (e.g. a Bluetooth headset is
   *  connected or removed) so the caller can warn / restart. */
  private onDeviceChange: (() => void) | null = null;
  private deviceChangeHandler: (() => void) | null = null;

  get recording(): boolean {
    return this.processor != null;
  }

  /** Best-effort: does the current input device look like a Bluetooth headset? */
  get bluetooth(): boolean {
    return this.isBluetooth;
  }

  /** Current mic input level, 0..1 (RMS of the latest analyser frame). 0 when
   *  not recording. Cheap to poll on an animation frame for a level meter. */
  getLevel(): number {
    const a = this.analyser, buf = this.levelBuf;
    if (!a || !buf) return 0;
    a.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
    return Math.min(1, Math.sqrt(sum / buf.length) * 2.5); // gentle gain so quiet speech reads
  }

  /**
   * Request mic permission + start capturing. Throws on denial/unavailable.
   *
   * @param opts.deviceId  Specific input device to use (from enumerateDevices).
   *   Passed as a NON-`exact` preference so the browser can fall back rather
   *   than throw OverconstrainedError if the device just vanished.
   * @param opts.onDeviceChange  Called when the OS audio-device set changes
   *   (a Bluetooth headset connecting/disconnecting mid-session) so the caller
   *   can surface a warning or restart on the new default.
   *
   * NB: we deliberately request only `audio: true` (plus an optional deviceId).
   * We do NOT force sampleRate / channelCount / echoCancellation. A Bluetooth
   * mic in Hands-Free Profile is mono / low-bitrate; pinning those constraints
   * makes the negotiation fail or hand back a dead track. Letting the device
   * pick its own profile is what keeps Bluetooth working — we resample to
   * 16 kHz ourselves in stop().
   */
  async start(opts?: { deviceId?: string; onDeviceChange?: () => void }): Promise<void> {
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== "function") {
      // Insecure context (http://) or unsupported browser — surfaced by micErrorMessage.
      throw new DOMException("getUserMedia unavailable", "NotSupportedError");
    }
    const audio: MediaTrackConstraints = {};
    if (opts?.deviceId) audio.deviceId = opts.deviceId; // preference, not { exact } — allow fallback.
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: Object.keys(audio).length ? audio : true,
    });
    // Label the input device (best-effort — only present post-permission) so a
    // silent take can be blamed on Bluetooth/HFP specifically.
    const track = this.stream.getAudioTracks()[0];
    const label = track?.label || "";
    this.isBluetooth = labelLooksBluetooth(label);
    // Re-request the mic if the user swaps to/from a Bluetooth headset mid-take.
    if (opts?.onDeviceChange && typeof navigator.mediaDevices.addEventListener === "function") {
      this.onDeviceChange = opts.onDeviceChange;
      this.deviceChangeHandler = () => { this.onDeviceChange?.(); };
      navigator.mediaDevices.addEventListener("devicechange", this.deviceChangeHandler);
    }
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new AC();
    this.inRate = this.ctx.sampleRate;
    this.source = this.ctx.createMediaStreamSource(this.stream);
    // Live input level (drives the recording meter) — taps the same source node.
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 512;
    this.levelBuf = new Uint8Array(this.analyser.fftSize);
    this.source.connect(this.analyser);
    this.processor = this.ctx.createScriptProcessor(4096, 1, 1);
    this.chunks = [];
    this.sawAudio = false;
    this.processor.onaudioprocess = (e) => {
      const data = e.inputBuffer.getChannelData(0);
      if (!this.sawAudio) {
        // Cheap live liveness check: a Bluetooth HFP dead track hands back all
        // zeros forever. One clearly-nonzero sample marks the stream as alive.
        for (let i = 0; i < data.length; i++) { if (Math.abs(data[i]) > 0.002) { this.sawAudio = true; break; } }
      }
      this.chunks.push(new Float32Array(data));
    };
    this.source.connect(this.processor);
    this.processor.connect(this.ctx.destination);
    // The AudioContext is created AFTER `await getUserMedia`, which breaks the
    // user-gesture chain — Chrome then starts it 'suspended', so onaudioprocess
    // never fires and we capture pure silence (→ empty transcript, "couldn't
    // extract text"). Explicitly resume so real audio is recorded.
    if (this.ctx.state === "suspended") {
      try { await this.ctx.resume(); } catch { /* best-effort — capture may still start */ }
    }
  }

  /** Live liveness signal: true once any non-zero sample has been captured this
   *  take. Poll while recording to detect a Bluetooth HFP dead-mic early (stays
   *  false past the first second or two of a dead stream). */
  get hasCapturedAudio(): boolean {
    return this.sawAudio;
  }

  /**
   * Stop + return a 16kHz mono WAV blob, OR a { noAudio } marker when the take
   * was silent so the caller can show the right message (Bluetooth-specific
   * when the input looked like a headset). Null is never returned now — the
   * discriminated result is easier to branch on than null-vs-blob.
   */
  async stop(): Promise<Blob | { noAudio: NoAudioReason }> {
    const wasBluetooth = this.isBluetooth;
    this.detachDeviceChange();
    this.processor?.disconnect();
    this.analyser?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    const ctx = this.ctx;
    this.processor = null;
    this.analyser = null;
    this.levelBuf = null;
    this.source = null;
    this.stream = null;
    if (ctx) { try { await ctx.close(); } catch { /* noop */ } }
    this.ctx = null;
    if (!this.chunks.length) return { noAudio: wasBluetooth ? "silent-bluetooth" : "silent" };

    // merge
    let len = 0;
    for (const c of this.chunks) len += c.length;
    const merged = new Float32Array(len);
    let off = 0;
    for (const c of this.chunks) { merged.set(c, off); off += c.length; }
    this.chunks = [];

    // Silence guard: a suspended context, a muted/dead mic, or a Bluetooth HFP
    // dead track yields all-zero samples. Encoding that wastes a transcription
    // round-trip and surfaces a confusing "couldn't extract text" — return the
    // noAudio marker instead. Threshold ~-54dBFS clears even quiet speech. When
    // the device looked like a headset, blame Bluetooth so the fix is obvious.
    let peak = 0;
    for (let i = 0; i < merged.length; i++) { const v = Math.abs(merged[i]); if (v > peak) peak = v; }
    if (peak < 0.002) return { noAudio: wasBluetooth ? "silent-bluetooth" : "silent" };

    const down = this.inRate > TARGET_RATE ? downsample(merged, this.inRate, TARGET_RATE) : merged;
    return encodeWav(down, this.inRate > TARGET_RATE ? TARGET_RATE : this.inRate);
  }

  /** Remove the devicechange listener if one was attached. Idempotent. */
  private detachDeviceChange(): void {
    if (this.deviceChangeHandler && navigator.mediaDevices
        && typeof navigator.mediaDevices.removeEventListener === "function") {
      navigator.mediaDevices.removeEventListener("devicechange", this.deviceChangeHandler);
    }
    this.deviceChangeHandler = null;
    this.onDeviceChange = null;
  }

  cancel(): void {
    this.detachDeviceChange();
    this.processor?.disconnect();
    this.analyser?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    if (this.ctx) { try { void this.ctx.close(); } catch { /* noop */ } }
    this.processor = null; this.analyser = null; this.levelBuf = null;
    this.source = null; this.stream = null; this.ctx = null; this.chunks = [];
    this.sawAudio = false; this.isBluetooth = false;
  }
}

function downsample(buf: Float32Array, from: number, to: number): Float32Array {
  const ratio = from / to;
  const out = new Float32Array(Math.round(buf.length / ratio));
  let oi = 0, i = 0;
  while (oi < out.length) {
    const next = Math.round((oi + 1) * ratio);
    let sum = 0, n = 0;
    for (; i < next && i < buf.length; i++) { sum += buf[i]; n++; }
    out[oi++] = n ? sum / n : 0;
  }
  return out;
}

function encodeWav(samples: Float32Array, rate: number): Blob {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(buf);
  const w = (off: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i)); };
  w(0, "RIFF"); v.setUint32(4, 36 + samples.length * 2, true); w(8, "WAVE");
  w(12, "fmt "); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  w(36, "data"); v.setUint32(40, samples.length * 2, true);
  let off = 44;
  for (let i = 0; i < samples.length; i++, off += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    v.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([buf], { type: "audio/wav" });
}
